import { ChatInput } from "@jugl-web/domain-resources/chat-bot/components/ChatBotUI/components/ChatInput";
import { useAgentImageSource } from "@jugl-web/domain-resources/chat-bot/hooks/chatBotMediaSourceResolvers";
import { resolveChatBotSettings } from "@jugl-web/domain-resources/chat-bot/utils/resolveChatBotSettings";
import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import { Avatar, DelayedRender, PlainButton } from "@jugl-web/ui-components";
import { cx, getUniqueId, useToast, useTranslations } from "@jugl-web/utils";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useInstagramAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/InstagramAccountProvider/useInstagramAccount";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import { createPlaygroundMessagesStoreHook } from "@web-src/modules/cx/utils/createPlaygroundMesagesStoreHook";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VList, VListHandle } from "virtua";
import AIIcon from "./assets/ai.svg?url";
import { ReactComponent as RestartIcon } from "./assets/restart.svg";
import UserIcon from "./assets/user.svg?url";
import whatsappBg from "./assets/whatsapp-bg.webp";
import { ChatBotPreviewMessageBubble } from "./components/ChatBotPreviewMessageBubble";
import { ChatBotPreviewTypingBubble } from "./components/ChatBotPreviewTypingBubble";
import { WELCOME_MESSAGE_ID, channelPreviewThemes } from "./consts";
import { ChatBotPreviewMessage } from "./types";

const PREVIEW_USER_NAME = "James Kenter";

interface GenericChannelPreviewProps {
  entityId: string;
  channel: Exclude<CRMChannel, CRMChannel.chatbot>;
}

const INITIAL_MESSAGES: ChatBotPreviewMessage[] = [
  {
    id: WELCOME_MESSAGE_ID,
    isOutgoing: false,
    text: "",
    username: "",
    avatar: "",
  },
];

const useGenericChannelPreviewMessagesStore =
  createPlaygroundMessagesStoreHook<ChatBotPreviewMessage>(INITIAL_MESSAGES);

/**
 * Interactive AI chat preview for non-chatbot channels (WhatsApp, Instagram, Twilio)
 */
export const GenericChannelPreview: FC<GenericChannelPreviewProps> = ({
  entityId,
  channel,
}) => {
  const chatRef = useRef<VListHandle>(null);

  const { t } = useTranslations();
  const { toast } = useToast();

  const { aiWorkflowsApi, integrationsApi } = useRestApiProvider();

  const [sendAgentChatMessage, { isLoading: isSendAgentChatMessageLoading }] =
    aiWorkflowsApi.useSendAgentChatMessageMutation();

  const { data: agentConfig } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
  });

  const { webChatBotAccount } = useWebChatBotAccount();
  const { instagramAccount } = useInstagramAccount();
  const { whatsappAgent } = useWhatsappAccount();
  const { crmConfig } = useManageAccountsDrawer();
  const twilioAccount = crmConfig?.twilio?.id ? crmConfig.twilio : undefined;

  const settings = resolveChatBotSettings(webChatBotAccount?.settings);
  const agentImageSrc = useAgentImageSource(settings.agentImage);

  const { chatName, chatImage, agentName, agentImage } = useMemo(() => {
    switch (channel) {
      case CRMChannel.instagram:
        if (instagramAccount) {
          return {
            chatName: instagramAccount.username || "",
            chatImage: instagramAccount.profile_picture_url || "",
            agentName: instagramAccount.username || "",
            agentImage: instagramAccount.profile_picture_url || "",
          };
        }
        break;

      case CRMChannel.whatsapp:
        if (whatsappAgent) {
          return {
            chatName: whatsappAgent.name || "",
            chatImage: whatsappAgent.profile?.[0]?.profile_picture_url || "",
            agentName: whatsappAgent.name || "",
            agentImage: whatsappAgent.profile?.[0]?.profile_picture_url || "",
          };
        }
        break;

      case CRMChannel.twilio:
        if (twilioAccount) {
          return {
            chatName: twilioAccount.phone_num || "",
            chatImage: "",
            agentName: twilioAccount.phone_num || "",
            agentImage: "",
          };
        }
        break;

      default:
        break;
    }

    return {
      chatName: settings.chatName || "",
      chatImage: settings.chatImageUrl || "",
      agentName: settings.agentName || "",
      agentImage: agentImageSrc,
    };
  }, [
    agentImageSrc,
    channel,
    instagramAccount,
    settings.agentName,
    settings.chatImageUrl,
    settings.chatName,
    twilioAccount,
    whatsappAgent,
  ]);

  const theme = channelPreviewThemes[channel];

  const [inputValue, setInputValue] = useState("");

  const { messages, setMessages } = useGenericChannelPreviewMessagesStore(
    `${entityId}-${channel}`
  );

  const messagesRef = useRef(messages);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const scrollToLastMessage = useCallback(
    (align: "start" | "end", smooth: boolean) => {
      if (!chatRef.current || messagesRef.current.length === 0) {
        return;
      }

      chatRef.current.scrollToIndex(messagesRef.current.length - 1, {
        smooth,
        align,
      });
    },
    []
  );

  const [isResetting, setIsResetting] = useState(false);

  const displayMessages = useMemo<ChatBotPreviewMessage[]>(
    () =>
      messages.map((message) => {
        if (message.isOutgoing) {
          return message;
        }

        return {
          ...message,
          username: settings.agentName,
          avatar: agentImageSrc || AIIcon,
          ...(message.id === WELCOME_MESSAGE_ID
            ? { text: settings.welcomeMessage }
            : {}),
        };
      }),
    [agentImageSrc, messages, settings.agentName, settings.welcomeMessage]
  );

  const typingDelayMs = useMemo(
    () => Math.random() * 1500 + 500,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSendAgentChatMessageLoading]
  );

  const resetMessages = useCallback(async () => {
    try {
      setIsResetting(true);

      await sendAgentChatMessage({
        entityId,
        // Sending "reset" instructs the agent to clear its conversation context
        input: "reset",
        medium: channel,
        business_name: agentConfig?.config.business_name || "",
        knowledge_id: entityId,
        agent_id: agentConfig?.agent_id || entityId,
        instructions: agentConfig?.config.instructions[0]?.value || "",
      }).unwrap();

      setMessages(INITIAL_MESSAGES);
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    } finally {
      setIsResetting(false);
    }
  }, [
    sendAgentChatMessage,
    entityId,
    channel,
    agentConfig,
    setMessages,
    toast,
    t,
  ]);

  const handleSendMessage = useCallback(async () => {
    const optimisticMessage: ChatBotPreviewMessage = {
      id: getUniqueId(),
      text: inputValue,
      isOutgoing: true,
      username: PREVIEW_USER_NAME,
      avatar: UserIcon,
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setInputValue("");

    window.setTimeout(() => {
      scrollToLastMessage("start", true);
    }, 100);

    try {
      const resp = await sendAgentChatMessage({
        entityId,
        input: optimisticMessage.text,
        medium: channel,
        business_name: agentConfig?.config.business_name || "",
        knowledge_id: entityId,
        agent_id: agentConfig?.agent_id || entityId,
        instructions: agentConfig?.config.instructions[0]?.value || "",
      }).unwrap();

      const botResponse: ChatBotPreviewMessage = {
        id: getUniqueId(),
        text: resp.agent_response,
        isOutgoing: false,
        username: "",
        avatar: "",
      };

      setMessages((prev) => [...prev, botResponse]);

      window.setTimeout(() => {
        scrollToLastMessage("start", true);
      }, 100);
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticMessage.id));

      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [
    inputValue,
    setMessages,
    scrollToLastMessage,
    sendAgentChatMessage,
    entityId,
    channel,
    agentConfig,
    toast,
    t,
  ]);

  useEffect(() => {
    scrollToLastMessage("end", false);
  }, [scrollToLastMessage]);

  return (
    <div className="flex h-full w-full items-center justify-center py-6">
      {/* Phone frame */}
      <div
        className="animate-fade-in flex h-full max-h-[800px] w-[410px] shrink-0 flex-col overflow-hidden rounded-[38px] border-[4px] border-solid border-[#F2F2F2] bg-white"
        style={{
          backgroundImage:
            channel === CRMChannel.whatsapp ? `url(${whatsappBg})` : undefined,
          backgroundSize: "cover",
        }}
      >
        {/* Header */}
        <div
          className={cx(
            "flex h-16 w-full shrink-0 items-center justify-between gap-2 border-0 border-b border-solid border-[#F2F2F2] px-4",
            theme.headerBg
          )}
        >
          {/* Header title */}
          <div className="flex items-center gap-2.5 overflow-hidden">
            <Avatar
              username="User"
              size="md"
              imageUrl={chatImage || AIIcon}
              className="border-dark-200 border border-solid"
            />
            <span
              className={cx(
                "font-secondary truncate text-[15px] font-medium leading-[140%]",
                theme.headerTextColor
              )}
            >
              {chatName || "Support"}
            </span>
          </div>
          {/* Restart button */}
          <PlainButton
            className="active:bg-grey-200 hover:bg-grey-100 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            onClick={resetMessages}
            disabled={isResetting}
          >
            <RestartIcon className={theme.headerIconColor} />
          </PlainButton>
        </div>
        {/* Message list */}
        <VList
          overscan={10}
          className="jugl__custom-scrollbar grow px-2 pb-4"
          reverse
          ref={chatRef}
        >
          {displayMessages.map((message) => (
            <ChatBotPreviewMessageBubble
              key={message.id}
              message={message}
              showAvatar
              theme={theme}
            />
          ))}
          {isSendAgentChatMessageLoading && !isResetting && (
            <DelayedRender
              isLoading
              delayMs={typingDelayMs}
              loadingContent={
                <ChatBotPreviewTypingBubble
                  username={agentName || "Agent"}
                  avatarUrl={agentImage || AIIcon}
                  theme={theme}
                />
              }
            />
          )}
        </VList>
        {/* Chat input */}
        <div className="px-4 pb-4">
          <ChatInput
            onChange={setInputValue}
            onSend={handleSendMessage}
            value={inputValue}
            isDisabled={isSendAgentChatMessageLoading}
          />
        </div>
      </div>
    </div>
  );
};
